import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import Typography from "@mui/material/Typography"
import { themeConfig }from '@gap/shared/theme'
import Card from '@mui/material/Card'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { SvgIconTypeMap } from '@mui/material/SvgIcon'
import SkeletonLoader from 'reusable/mui/skeleton/SkeletonLoader'
import Tooltip from '@mui/material/Tooltip'

interface ShareDocumentDialogExportCardProps {
   title: string
   subTitle?: string
   mainIcon: OverridableComponent<SvgIconTypeMap<{}, "svg">> | string
   rightComponent: React.ReactNode
   loading?: boolean
   disabled?: boolean
   disabledText?: string
   onCardClick: () => void
}

export default observer(function ShareDocumentDialogExportCard(props: ShareDocumentDialogExportCardProps) {
   if (props.loading) {
      return <SkeletonLoader loading={true} sx={{ height: 78.5, transform: 'none', borderRadius: '16px' }} />
   }

   const handleClick = () => {
      if (!props.disabled) {
         props.onCardClick()
      }
   }

   const cardComponent = <Card
      sx={{
         display: 'flex',
         alignItems: 'flex-start',
         p: 2,
         borderRadius: '16px',
         justifyContent: 'space-between',
         border: `1px solid ${themeConfig.primary[200]}`,
         mb: 2,
         cursor: props.disabled ? 'default' : 'pointer',
         opacity: props.disabled ? 0.6 : 1,
         pointerEvents: props.disabled ? 'none' : 'auto',
         transition: 'opacity 0.2s ease-in-out',
         ...(!props.disabled && {
            ':hover': {
               backgroundColor: themeConfig.primary[100],
            }
         })
      }}
      onClick={handleClick}
      elevation={0}
   >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
         {typeof props.mainIcon === 'string' && <img src={props.mainIcon} width="35" />}
         {typeof props.mainIcon !== 'string' && <props.mainIcon fontSize="large" />}
         <Box sx={{ ml: 2 }}>
            <Typography fontWeight={600}>{props.title}</Typography>
            <Typography variant="body2" color="text.secondary">
               {props.subTitle}
            </Typography>
         </Box>
      </Box>

      {props.rightComponent}
   </Card>

   if (props.disabled && props.disabledText) {
      return (
         <Tooltip title={props.disabledText} arrow>
            <span>{cardComponent}</span>
         </Tooltip>
      )
   }

   return cardComponent
})